import React, { Component } from "react";
import ReactDOM from "react-dom";
import { Link, withRouter } from "react-router-dom";
import HelperClass from "../../../config/helperClass";
import { listLeadsKanban, updateStage } from "./api/leads_api";
import NotificationToast from "../../../components/Reseller/NotificationToast";

const auth = new HelperClass();

/* ─── helpers ─────────────────────────────────────────────────────────── */
function fmtMoney(v) {
  var n = parseFloat(v) || 0;
  if (n >= 1000000) return "$" + (n / 1000000).toFixed(1) + "M";
  if (n >= 1000) return "$" + (n / 1000).toFixed(1) + "k";
  return "$" + n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function priorityBadge(p) {
  var map = { critical: ["#ef4444", "#fef2f2"], high: ["#f97316", "#fff7ed"], medium: ["#d97706", "#fef3c7"], med: ["#d97706", "#fef3c7"], low: ["#6b7280", "#f9fafb"] };
  var pair = map[String(p).toLowerCase()] || map.low;
  return (
    <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", padding: "2px 6px", borderRadius: 4, background: pair[1], color: pair[0], marginLeft: 4 }}>
      {p || "low"}
    </span>
  );
}

function dateLabel(d) {
  if (!d) return null;
  var now = new Date();
  var dt = new Date(d);
  var diff = Math.floor((dt - now) / 86400000);
  var str = dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  var color = diff < 0 ? "#ef4444" : diff === 0 ? "#d97706" : "#6b7280";
  return <span style={{ fontSize: 10, color: color }}>{str}</span>;
}

/* ─── KanbanBoard ─────────────────────────────────────────────────────── */
/*
 * Props:
 *   filters      {object}   – { partner_accountno, priority, per_column, refreshKey }
 *                             Changes trigger a re-fetch (compared via JSON.stringify).
 *   onAddLead    {function} – Called when "Add Lead" button is clicked inside a column.
 *   showAddBtn   {bool}     – Whether to render "Add Lead" buttons inside columns.
 */
class KanbanBoard extends Component {
  state = {
    columns: [],
    loading: true,
    error: null,
    dragging: null,
    dragOverCol: null,
    saving: {},
    convertPrompt: null,
    toast: null,
    loadingMore: {},
  };

  componentDidMount() {
    this.fetchBoard();
  }

  componentDidUpdate(prevProps) {
    var prev = JSON.stringify(prevProps.filters || {});
    var cur = JSON.stringify(this.props.filters || {});
    if (prev !== cur) this.fetchBoard();
  }

  fetchBoard = () => {
    var self = this;
    self.setState({ loading: true, error: null });
    var f = self.props.filters || {};
    var payload = { per_column: f.per_column || 20 };
    if (f.partner_accountno) payload.partner_accountno = f.partner_accountno;
    if (f.priority) payload.priority = f.priority;
    if (f.search) payload.search = f.search;

    listLeadsKanban(auth.getAccount(), auth.getToken(), payload)
      .then(function(r) {
        var d = r && r.data;
        if (d && d.status === 200) {
          self.setState({ columns: d.data.columns || [], loading: false });
        } else {
          self.setState({ loading: false, error: "Failed to load pipeline." });
        }
      })
      .catch(function() {
        self.setState({ loading: false, error: "Network error loading pipeline." });
      });
  };

  /* ─── drag events ─────────────────────────────────────────────────── */
  onDragStart = (e, card, fromKey) => {
    this.lastDragAt = Date.now();
    this.setState({ dragging: { card: card, fromKey: fromKey } });
    e.dataTransfer.effectAllowed = "move";
  };

  onDragOver = (e, key) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (this.state.dragOverCol !== key) this.setState({ dragOverCol: key });
  };

  onDragLeave = (e, key) => {
    if (this.state.dragOverCol === key) this.setState({ dragOverCol: null });
  };

  onDrop = (e, toKey) => {
    e.preventDefault();
    this.lastDragAt = Date.now();
    this.setState({ dragOverCol: null });
    var dragging = this.state.dragging;
    if (!dragging) return;
    if (dragging.fromKey === toKey) { this.setState({ dragging: null }); return; }
    this.moveCard(dragging.card, dragging.fromKey, toKey);
    this.setState({ dragging: null });
  };

  onDragEnd = () => {
    this.lastDragAt = Date.now();
    this.setState({ dragging: null, dragOverCol: null });
  };

  onCardClick = (card) => {
    if (!card || !card.lead_uid) return;
    if (this.state.dragging) return;
    if (this.lastDragAt && (Date.now() - this.lastDragAt) < 220) return;
    this.props.history.push("/crm_lead_detail/" + card.lead_uid);
  };

  /* ─── move card (optimistic + 409 revert) ────────────────────────── */
  moveCard = (card, fromKey, toKey) => {
    var self = this;
    var prevColumns = self.state.columns.map(function(col) {
      return Object.assign({}, col, { cards: col.cards.slice() });
    });

    var columns = prevColumns.map(function(col) {
      if (col.stage.stage_key === fromKey) {
        return Object.assign({}, col, {
          cards: col.cards.filter(function(c) { return c.id !== card.id; }),
          count: Math.max(0, col.count - 1),
          value_total: col.value_total - (parseFloat(card.deal_value) || 0),
        });
      }
      if (col.stage.stage_key === toKey) {
        return Object.assign({}, col, {
          cards: [Object.assign({}, card, { stage_key: toKey })].concat(col.cards),
          count: col.count + 1,
          value_total: col.value_total + (parseFloat(card.deal_value) || 0),
        });
      }
      return col;
    });

    var saving = Object.assign({}, self.state.saving);
    saving[card.id] = true;
    self.setState({ columns: columns, saving: saving });

    updateStage(auth.getAccount(), auth.getToken(), {
      lead_id: card.id,
      stage_key: toKey,
    })
      .then(function(r) {
        var d = r && r.data;
        if (d && d.status === 200) {
          if (d.data && d.data.prompt_convert) {
            self.setState({ convertPrompt: { lead_id: card.id, lead_uid: card.lead_uid, company: card.company || card.contact_name } });
          } else {
            self.showToast("Stage updated.", "success");
          }
        } else if (d && d.status === 409) {
          self.setState({ columns: prevColumns });
          self.showToast("Conflict — refreshing\u2026", "error");
          setTimeout(self.fetchBoard, 1500);
        } else {
          self.setState({ columns: prevColumns });
          self.showToast("Failed to update stage.", "error");
        }
      })
      .catch(function() {
        self.setState({ columns: prevColumns });
        self.showToast("Network error.", "error");
      })
      .then(function() {
        var s2 = Object.assign({}, self.state.saving);
        delete s2[card.id];
        self.setState({ saving: s2 });
      });
  };

  /* ─── load more per column ────────────────────────────────────────── */
  loadMoreColumn = (stageKey, currentCount) => {
    var self = this;
    var lm = Object.assign({}, self.state.loadingMore);
    lm[stageKey] = true;
    self.setState({ loadingMore: lm });

    var f = self.props.filters || {};
    listLeadsKanban(auth.getAccount(), auth.getToken(), {
      per_column: currentCount + (f.per_column || 20),
      filter_stage: stageKey,
    })
      .then(function(r) {
        var d = r && r.data;
        if (d && d.status === 200) {
          var fresh = (d.data.columns || []).filter(function(c) { return c.stage.stage_key === stageKey; })[0];
          if (fresh) {
            self.setState(function(prev) {
              return {
                columns: prev.columns.map(function(col) {
                  return col.stage.stage_key === stageKey ? fresh : col;
                }),
              };
            });
          }
        }
      })
      .then(function() {
        var lm2 = Object.assign({}, self.state.loadingMore);
        delete lm2[stageKey];
        self.setState({ loadingMore: lm2 });
      })
      .catch(function() {
        var lm2 = Object.assign({}, self.state.loadingMore);
        delete lm2[stageKey];
        self.setState({ loadingMore: lm2 });
      });
  };

  /* ─── toast ───────────────────────────────────────────────────────── */
  showToast = (msg, type) => {
    clearTimeout(this._toastTimer);
    this.setState({ toast: { msg, type } });
    this._toastTimer = setTimeout(() => this.setState({ toast: null }), 2500);
  };

  /* ─── render ─────────────────────────────────────────────────────── */
  render() {
    var self = this;
    var state = self.state;
    var columns = state.columns;
    var loading = state.loading;
    var error = state.error;
    var dragging = state.dragging;
    var dragOverCol = state.dragOverCol;
    var saving = state.saving;
    var convertPrompt = state.convertPrompt;
    var toast = state.toast;
    var loadingMore = state.loadingMore;
    var onAddLead = self.props.onAddLead;
    var showAddBtn = self.props.showAddBtn;
    var isDraggingAny = dragging !== null;
    var fullHeight = !!self.props.fullHeight;

    return (
      <div style={fullHeight ? { position: "relative", height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" } : { position: "relative" }}>

        {/* ── toast ── */}
        {ReactDOM.createPortal(
          <div style={{ position: "fixed", top: 24, right: 24, zIndex: 9999, width: 340 }}>
            <NotificationToast
              type={toast ? toast.type : "success"}
              message={toast ? toast.msg : ""}
              duration={2000}
              onDismiss={() => this.setState({ toast: null })}
            />
          </div>,
          document.body
        )}

        {/* ── error ── */}
        {error && (
          <div className="alert alert-danger" style={{ marginBottom: 16 }}>
            {error}&nbsp;
            <button className="btn btn-sm btn-danger" onClick={self.fetchBoard}>Retry</button>
          </div>
        )}

        {/* ── loading ── */}
        {loading && (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
            <span style={{ marginLeft: 10, color: "#888" }}>Loading pipeline\u2026</span>
          </div>
        )}

        {/* ── board ── */}
        {!loading && columns.length > 0 && (
          <div style={fullHeight
            ? { display: "flex", gap: 12, overflowX: "auto", overflowY: "hidden", paddingBottom: 8, alignItems: "flex-start", flex: 1 }
            : { display: "flex", gap: 12, overflowX: "auto", paddingBottom: 8, alignItems: "flex-start" }
          }>
            {columns.map(function(col) {
              var sk = col.stage.stage_key;
              var stageColor = col.stage.color_hex || col.stage.color || "#6b7280";
              var isOver = dragOverCol === sk;

              return (
                <div
                  key={sk}
                  style={{
                    minWidth: 260, maxWidth: 280, flex: "0 0 260px",
                    background: isOver ? "#f0f4ff" : "#f8f9fa",
                    borderRadius: 10,
                    border: "2px solid " + (isOver ? stageColor : "#e5e7eb"),
                    transition: "border-color 0.15s, background 0.15s",
                    display: "flex", flexDirection: "column",
                    height: fullHeight ? "100%" : "auto",
                    overflow: fullHeight ? "hidden" : "visible",
                  }}
                  onDragOver={function(e) { self.onDragOver(e, sk); }}
                  onDragLeave={function(e) { self.onDragLeave(e, sk); }}
                  onDrop={function(e) { self.onDrop(e, sk); }}
                >
                  {/* column header */}
                  <div style={{
                    padding: "10px 12px 8px",
                    borderBottom: "2px solid " + stageColor,
                    borderRadius: "8px 8px 0 0",
                    background: "#fff",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                        <span style={{ width: 10, height: 10, borderRadius: "50%", background: stageColor, display: "inline-block" }}></span>
                        <span style={{ fontWeight: 700, fontSize: 13, color: "#1f2937" }}>{col.stage.label}</span>
                      </div>
                      <span style={{
                        background: stageColor + "22",
                        color: stageColor,
                        fontSize: 11, fontWeight: 700, padding: "2px 7px", borderRadius: 12,
                      }}>{col.count}</span>
                    </div>
                    <div style={{ fontSize: 11, color: "#6b7280", marginTop: 3 }}>{fmtMoney(col.value_total)}</div>
                  </div>

                  {/* cards */}
                  <div style={{ padding: "8px 8px 4px", flex: 1, minHeight: 80, overflowY: fullHeight ? "auto" : "visible" }}>
                    {col.cards.length === 0 && !isDraggingAny && (
                      <div style={{ textAlign: "center", padding: "20px 0", color: "#9ca3af", fontSize: 12 }}>No leads</div>
                    )}
                    {col.cards.length === 0 && isDraggingAny && (
                      <div style={{ border: "2px dashed " + stageColor, borderRadius: 8, height: 60, marginBottom: 4, opacity: 0.4 }}></div>
                    )}
                    {col.cards.map(function(card) {
                      var isSaving = saving[card.id];
                      return (
                        <div
                          key={card.id}
                          draggable={!isSaving}
                          onDragStart={!isSaving ? function(e) { self.onDragStart(e, card, sk); } : null}
                          onDragEnd={self.onDragEnd}
                          onClick={function() { self.onCardClick(card); }}
                          style={{
                            background: "#fff",
                            borderRadius: 8,
                            border: "1px solid #e5e7eb",
                            padding: "10px 12px",
                            marginBottom: 8,
                            cursor: isSaving ? "wait" : "pointer",
                            opacity: isSaving ? 0.6 : 1,
                            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                            transition: "opacity 0.2s",
                            borderLeft: "3px solid " + stageColor,
                          }}
                        >
                          <div style={{ fontWeight: 700, fontSize: 13, color: "#111827", marginBottom: 3, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 6 }}>
                            <span style={{ color: "#111827", wordBreak: "break-word", overflowWrap: "anywhere", lineHeight: 1.35, maxWidth: "calc(100% - 52px)" }}>
                              {card.company || card.contact_name || "\u2014"}
                            </span>
                            {priorityBadge(card.priority)}
                          </div>
                          {card.company && card.contact_name && (
                            <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4, wordBreak: "break-word", overflowWrap: "anywhere" }}>{card.contact_name}</div>
                          )}
                          {card.deal_value > 0 && (
                            <div style={{ fontSize: 12, fontWeight: 600, color: "#1a7a4a", marginBottom: 4 }}>{fmtMoney(card.deal_value)}</div>
                          )}
                          {card.next_action_date && (
                            <div style={{ marginBottom: 4 }}>
                              <em className="icon ni ni-calendar" style={{ fontSize: 11, color: "#9ca3af", marginRight: 3 }}></em>
                              {dateLabel(card.next_action_date)}
                            </div>
                          )}
                          {card.source && (
                            <div style={{ fontSize: 10, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em" }}>{card.source}</div>
                          )}
                          {isSaving && (
                            <div style={{ marginTop: 4 }}>
                              <div className="spinner-border spinner-border-sm text-primary" style={{ width: 12, height: 12 }} role="status"></div>
                              <span style={{ fontSize: 10, color: "#6b7280", marginLeft: 5 }}>Saving\u2026</span>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* load more */}
                    {col.has_more && (
                      <button
                        className="btn btn-sm btn-light w-100"
                        style={{ fontSize: 11, marginTop: 4 }}
                        disabled={loadingMore[sk]}
                        onClick={function() { self.loadMoreColumn(sk, col.cards.length); }}
                      >
                        {loadingMore[sk]
                          ? <span><div className="spinner-border spinner-border-sm" style={{ width: 10, height: 10 }}></div> Loading\u2026</span>
                          : <span>Load more ({col.count - col.cards.length} remaining)</span>
                        }
                      </button>
                    )}

                    {/* add lead btn */}
                    {showAddBtn && onAddLead && (
                      <button
                        onClick={function() { onAddLead(sk); }}
                        className="btn btn-sm btn-outline-primary w-100"
                        style={{ fontSize: 12, marginTop: col.has_more ? 6 : 4, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}
                      >
                        <em className="icon ni ni-plus"></em>
                        <span>Add Lead</span>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── empty state ── */}
        {!loading && !error && columns.length === 0 && (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <em className="icon ni ni-kanban" style={{ fontSize: 40, color: "#d1d5db" }}></em>
            <p style={{ color: "#6b7280", marginTop: 10, fontSize: 13 }}>
              No pipeline stages configured.&nbsp;
              <Link to="/crm_leads_settings">Set up stages</Link>.
            </p>
          </div>
        )}

        {/* ── convert prompt modal ── */}
        {convertPrompt && (
          <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.45)" }}>
            <div className="modal-dialog modal-sm modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header" style={{ borderBottom: "2px solid #1a7a4a" }}>
                  <h5 className="modal-title" style={{ color: "#1a7a4a" }}>
                    <em className="icon ni ni-check-circle me-1"></em> Mark as Won?
                  </h5>
                </div>
                <div className="modal-body">
                  <p style={{ fontSize: 14 }}><strong>{convertPrompt.company}</strong> has been moved to the Won stage.</p>
                  <p style={{ fontSize: 13, color: "#6b7280" }}>Would you like to convert this lead into an active account?</p>
                </div>
                <div className="modal-footer" style={{ gap: 8 }}>
                  <button
                    className="btn btn-sm btn-outline-light"
                    onClick={function() { self.setState({ convertPrompt: null }); self.showToast("Marked as Won.", "success"); }}
                  >
                    Not now
                  </button>
                  <Link
                    to={"/crm_lead_detail/" + (convertPrompt.lead_uid || "")}
                    className="btn btn-sm btn-success"
                    onClick={function() { self.setState({ convertPrompt: null }); }}
                  >
                    View &amp; Convert
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default withRouter(KanbanBoard);
